import {ReactNode} from 'react';

import {
  PINNED_RIGHT_EDGE_LINE_STYLE,
  ROW_BORDER_BOTTOM,
} from '../../../../../../../../constants/borderStyles';
import {useGridKeyboardNav} from '../../../../../../../../context/GridKeyboardNavContext';
import {getStickyCellBackgroundClass} from '../../../../../../../../utils/getStickyCellBackgroundClass';

export type ActionColumnCellProps = {
  width: number;
  heightPx: number;
  rowIndex: number;
  children: ReactNode;
  horizontalSeparators: boolean;
  isSelected?: boolean;
  isHighlighted?: boolean;
};

/** Left-edge drop shadow marking the sticky action column's boundary. */
const ACTION_COLUMN_DROP_SHADOW = '-2px 0 4px -2px rgba(0,0,0,0.15)';

// Module-level: stable identity across every render. Re-creating this handler per render
// gave the <td> a fresh onClick prop on every cycle, which contributed to the click-handler
// churn behind the Maximum-update-depth loop when actions toggled lock state.
const stopRowClickPropagation = (event: React.MouseEvent) => {
  event.stopPropagation();
};

/**
 * Sticky <td> cell for the action column.
 * Owns all layout concerns: sticky positioning, drop shadow, edge line, width constraints,
 * and stopPropagation to prevent row click when interacting with action buttons.
 */
export function ActionColumnCell(props: ActionColumnCellProps) {
  const nav = useGridKeyboardNav();
  return (
    <td
      ref={(el) => nav?.registerActionCell(props.rowIndex, el)}
      className={`p-0 border-0 ${getStickyCellBackgroundClass(
        props.isSelected ?? false,
        props.isHighlighted ?? false
      )}`}
      style={{
        position: 'sticky',
        right: 0,
        zIndex: 'var(--z-sidebar)',
        width: props.width,
        maxWidth: props.width,
        height: props.heightPx,
        maxHeight: props.heightPx,
        boxShadow: ACTION_COLUMN_DROP_SHADOW,
        ...(props.horizontalSeparators ? ROW_BORDER_BOTTOM : {}),
      }}
      onClick={stopRowClickPropagation}
    >
      <span style={PINNED_RIGHT_EDGE_LINE_STYLE} />
      {props.children}
    </td>
  );
}
